import React, {createRef} from 'react';
import KeyCodes from '../../utils/KeyCodes.js';
import Messages from '../templates/Messages.jsx';
import KoreWebSocketManager from '../utils/KoreWebSocketManager.js';
import ChatEventCodes from '../utils/ChatEventCodes.js';
import KoreChatCookieManager from '../utils/KoreChatCookieManager.js';
import KoreAnalyticsManager from '../utils/KoreAnalyticsManager.js';
import AskUsInjector from '../utils/AskUsInjector.js';
import QueuePosition from '../templates/QueuePosition.jsx';
import AwaitingMessageHandler from '../utils/AwaitingMessageHandler.jsx';
import ChatWindowHeader from './ChatWindowHeader.jsx';

export default class ChatBot extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            /**@type {String} The BotName passsed through from the HTML injection.*/
            chatBot:  props.botName,
            /**@type {String} The Bot ID passsed through from the HTML injection.*/
            taskBotId: props.botId,
            /**@type {String} The root for the AGW API service on the initial root element. */
            apiRootUrl: props.apiRootUrl,
            /**@type {String} The root for the CSS file on the initial root element. */
            cssUrl: props.cssUrl,
            /**@type {String} The indicator from the login system is the user has authenticated previously during chat.*/
            chatAuthenticated: props.chatAuthenticated,
            /** @type {Array<Object>} */
            messages: [],
            container: null,
            /** @type {HTMLElement} */
            fabButton: null,
            /** @type {Array<HTMLElement>} */
            externalFabButtons: [],
            /** @type {HTMLElement} */
            fabButtonMinimizedMessage: null,
            /**@type {HTMLDialogElement} */
            chatWindow: null,
            /**@type {Boolean} */
            isConnecting: false,
            /**@type {Boolean} */
            chatWindowOpen: false,
            /**@type {Boolean} */
            chatConnected: false,
            /**@type {Boolean} */
            chatClosed: false,
            /**@type {String} */
            webSocketUrl: '',
            /** @type {KoreWebSocketManager} */
            webSocketManager: null,
            /** @type {Boolean} */
            isReconnecting: false,
            /** @type {HTMLTextAreaElement} */
            userInput: null,
            /** @type {HTMLElement} */
            sendButton: null,
            /** @type {HTMLCollectionOf<HTMLElement>} */
            closeButtons: [],
            /**@type {Number} Keeps track of the times a user has clicked the 'x'. Used to intercept and return a survey is needed.*/
            closeButtonClickCount: 0,
            /**@type {Number} */
            maxCloseButtonClicks: 1,
            //Timeout ID to wait for a survey result.
            surveyResponseTimeout: undefined,
            surveyTimeoutMax: 3000,
            /** @type {HTMLCollectionOf<HTMLElement>} */
            minimizeButtons: [],
            lastMessageSender: undefined,
            /** @type {KoreChatCookieManager} */
            chatCookieManager: undefined,
            chatInProgressCookieName: '_chatInProgress',
            chatInProgressCookieTimeoutMinutes: 20,
            loginFlowInProgress: false,
            loginFlowSuccess: false,
            loginFlowInProgressCookieName: '_chatLoginFlowInProgress',
            /** @type {Number} Sets the time that the login flow in progress cookie will remain in minutes.*/
            loginFlowInProgessTimeout: 2,
            /** @type {KoreAnalyticsManager} */
            analyticsManager: undefined,
            queuePositionPayload: undefined,
            /** @type {Boolean} If the user is waiting for a response, then the will flag to true and reder the awaiting response section. */
            awaitingResponse: false,
            // Provides the check for obsolete browsers. Will not render if HTMLDialog is not usable.
            renderChat: typeof HTMLDialogElement === 'function'
        }

        this.newMessageLiveRegion = createRef();
        this.userTypingEventHandler = (e) => {this.handleUserTypingEvent(e)};
        this.typingEndedTimeout = undefined;
        this.typingEndedDelay = 4000;
        this.typingStartedInterval = undefined;
        // The delay to send TYPING_STARTED repeatedly. Found 1s prevents botKitUnreachable errors from occurring.
        this.typingStartedDelay = 1000;

        this.setWindowProperties();
    }

    /**
     * Sets the getter and setter for the window.chatAuthrnticated and window.chatApplyProduct
     * properties that are to be set by the consuming applications.
     */
    setWindowProperties() {
        window._chatAuthenticated = window.chatAuthenticated;
        Object.defineProperty(window, 'chatAuthenticated', {
            get: function() {
                return this._chatAuthenticated;
            },
            set: function(value) {
                this._chatAuthenticated = value;
            }
        });
        window._chatApplyProduct = window.chatApplyProduct;
        Object.defineProperty(window, 'chatApplyProduct', {
            get: function() {
                return this._chatApplyProduct;
            },
            set: function(value) {
                this._chatApplyProduct = value;
            }
        });
    }

    /**
     * Handles when the websocket disconnects under normal circumstances e.g. ping/pong timeout.
     */
    chatDisconnected() {
        this.setState({
                chatConnected: false,
                queuePositionPayload: undefined,
                awaitingResponse: false
            }, () => {
            this.state.userInput.value = '';
            this.state.fabButtonMinimizedMessage.classList.remove('minimized');
            this.state.chatCookieManager.deleteCookie(this.state.chatInProgressCookieName);
            this.clearTypingTimeouts();
        });
    }


    clearTypingTimeouts() {
        this.typingEndedTimeout = clearTimeout(this.typingEndedTimeout);
        this.typingStartedInterval = clearInterval(this.typingStartedInterval);
    }

    /**
     * Manually close the chat connection. Useful when the user clicks the 'X'.
     */
    closeConnection() {
        this.state.webSocketManager.closeConnection();
        this.setState({
            messages: [],
            lastMessageSender: undefined,
            queuePositionPayload: undefined,
            awaitingResponse: false
        }, () => {
            if (this.state.chatConnected === true) {
                this.state.webSocketManager.closeConnection();
                this.state.chatCookieManager.deleteCookie(this.state.chatInProgressCookieName);
                this.clearTypingTimeouts();
            }
            this.hideChatWindow();
            this.state.userInput.value = '';
        });
    }

    /**
     * Set the initial state once the component has mounted and once the state is set,
     * setup the event listeners.
     */
    componentDidMount() {
        /**
         * Don't want these to appear before the component mounts and also want to
         * have the before setting state so that the external fab buttons they inject
         * into ROL are present before gathering the collection.
         */
        AskUsInjector.setOnlineBankingButtons();
        const container = document.getElementById('kore-chat-bot').shadowRoot.getElementById('kore-chat-bot');
        this.setState({
            container: container,
            fabButton: container.querySelector('.kore-chat-bot__fab'),
            externalFabButtons: document.getElementsByClassName('fab-launcher'),
            fabButtonMinimizedMessage: container.querySelector('.chat-fab__minimized-message'),
            chatWindow: /**@type {HTMLDialogElement */(container.querySelector('.kore-chat-bot__chat-window')),
            userInput: container.querySelector('.chat-window__input'),
            sendButton: container.querySelector('.chat-window__send-message'),
            closeButtons: container.getElementsByClassName('chat-close'),
            minimizeButtons: container.getElementsByClassName('chat-minimize'),
            webSocketManager: new KoreWebSocketManager(this.state.taskBotId, this.state.apiRootUrl, this.renderChatHistory.bind(this)),
            chatCookieManager: new KoreChatCookieManager(),
            analyticsManager: new KoreAnalyticsManager()
        }, () => {
            // Get the value the chat started from the cookie. If invalid for parsing to an int then just continue without continuing the chat.
            const chatInProgressTime = parseInt(this.state.chatCookieManager.readcookie(this.state.chatInProgressCookieName));
            //Update the cookie if it exists and is valid, then show the chat. If not just delete it.
            if (chatInProgressTime && ((Date.now() - chatInProgressTime) <= (this.state.chatInProgressCookieTimeoutMinutes*60*1000))) {
                this.state.chatCookieManager.updateCookie(this.state.chatInProgressCookieName, Date.now());
                this.showChatWindow(true);
            } else {
                this.state.chatCookieManager.deleteCookie(this.state.chatInProgressCookieName);
            }
            this.setEventListeners();
            this.setAuthenticationStatus();
        });
    }

    /**
     * Hides the chat window and shows the FAB. Used when the chat is minimized OR closed.
     */
    hideChatWindow() {
        this.setState({chatWindowOpen: false});
        this.state.fabButton.classList.toggle('hidden');
        this.state.container.classList.toggle('open');
        this.state.chatWindow.close();
    }

    /**
     * Shows the chat window and hides the FAB. Used when the chat is opened. Also starts
     * up the connection IF chat is not already in progress.
     * @param {Boolean} continueChat The flag to continue chat or not.
     */
    showChatWindow(continueChat = false) {
        this.setState({chatWindowOpen: true, closeButtonClickCount: 0}, () => {
            this.state.fabButton.classList.toggle('hidden');
            this.state.fabButtonMinimizedMessage.classList.remove('minimized');
            this.state.container.classList.toggle('open');
            this.state.chatWindow.show();
            this.state.chatCookieManager.createCookie(this.state.chatInProgressCookieName, Date.now());
            if (!this.state.chatConnected) {
                this.setState({isConnecting: true}, () => {
                    try {
                        this.state.webSocketManager.createSocketConnection(continueChat);
                    }
                    catch(e) {
                        this.setState({isConnecting: false}, () => {
                            console.error(e);
                            document.dispatchEvent(new Event(ChatEventCodes.WebsocketServiceUnavailable));
                        });
                    }
                    finally {
                        if (!continueChat) {
                            this.state.analyticsManager.userOpenedChatWindow();
                        }
                    }
                });
            }
        });
    }

    /**
     * Removes the typing handler event funciton to the user input.
     */
    handleAgentSessionEnd() {
        this.state.userInput.removeEventListener('keydown', this.userTypingEventHandler);
    }

    /**
     * Binds the typing handler event funciton to the user input.
     */
    handleAgentSessionStart() {
        this.state.userInput.addEventListener('keydown', this.userTypingEventHandler);
    }

    /**
     * Handles any custom needs for when the specific Bot_Active message is returned from Kore.
     * For now this is only applicable to handling the loginf low ending and sending the user
     * merge event if needed. This is based on the message event listener from the web socket
     * manager and should bypass any timing issues between the chat connection and actually
     * getting a message from the WebSocket.
     * @param {{loginFlowSuccess: Boolean}} data The data sent through the message event.
     */
    handleBotActive(data) {
        this.setState({loginFlowSuccess: data?.loginFlowSuccess}, () => {
            this.handleLoginFlowEnded();
        });
    }

    /**
     * Creates the message object to be used to map the bot response. Then adds it into the
     * array of existing bot messages in state.
     * @param {Object} payload The bot response object from the WSS connection.
     */
    handleBotResponse(payload) {
        const templateType = payload.message[0]?.component?.payload?.payload?.template_type;
        //Clears the timeout set by the close button intercept in handleCloseButton() if a survey is received.
        if (templateType === 'postLiveChatSurveyTemplate' || templateType === 'botSurveyFeedbackTemplate') {
            clearTimeout(this.state.surveyResponseTimeout);
        }
        if (templateType === 'queuePosition_template') {
            this.setState({queuePositionPayload: payload.message[0]?.component?.payload?.payload, awaitingResponse: false});
            return;
        }
        let messageSender = 'bot';
        if (templateType === 'liveAgentMessage') {
            messageSender = payload.message[0].component.payload.payload.agent_name;

            /**
             * Reset the queue position template here. There is an Agent_Session_Start message that occurs
             * in the flow but it triggers when the user starts the overall agent flow, and not when they
             * receive their initial message. The means we have to wait until we get the first actual message
             * and then clear out queue position.
             */
            if (this.state.queuePositionPayload) {
                this.setState({queuePositionPayload: undefined});
            }
        }
        const message = {
            'messageSender': messageSender,
            'payload': payload.message[0],
            'messageTime': Date.now(),
            'lastMessageSender': this.state.lastMessageSender
        }
        this.setState({messages: [...this.state.messages, message], lastMessageSender: messageSender, awaitingResponse: false}, () => {
            this.state.chatWindow.querySelector('.chat-window__messages').lastElementChild.scrollIntoView({behavior: "smooth"});
        });
    }

    /**
     * This is a handler for an error that occurred when Kore went down mysteriously. This needs to handle
     * more but for now just catching it and observing it if it happens again is useful.
     * @param {Object} payload
     */
    handleBotKitUnreachable(payload) {
        const messageSender = 'bot';
        const message = {
            'messageSender': messageSender,
            'payload': payload,
            'messageTime': Date.now(),
            'lastMessageSender': this.state.lastMessageSender
        };
        this.setState({messages: [...this.state.messages, message], lastMessageSender: messageSender}, () => {
            this.state.chatWindow.querySelector('.chat-window__messages').lastElementChild.scrollIntoView({behavior: "smooth"});
        });
    }


    handleChatConnected() {
        const loginCookieActive = this.state.chatCookieManager.readcookie(this.state.loginFlowInProgressCookieName) !== '';
        this.setState({
            chatConnected: true,
            isConnecting: false,
            loginFlowInProgress: loginCookieActive
        });
    }

    /**
     * This controls the intercept of the close button to request a survey before the user closes the window.
     * If the socket hasn't been established and the chat isn't connected, we close the possible
     * connection and the window immediately. If the user clicks the close button, we set a timeout for
     * the max timeout wait and then request a survey. If we get a survey message type back in
     * handleBotResponse(), the timeout will be cleared. If not, the connection and window are closed.
     * Else if the user clicks on the close button more than the max setting, we close the
     * connection and the window regardless of survey status.
     */
    handleCloseButton() {
        if (!this.state.chatConnected) {
            this.state.webSocketManager.forceBlockSocket();
            this.closeConnection();
            return;
        }

        if(this.state.closeButtonClickCount < this.state.maxCloseButtonClicks) {
            const updatedClickCount = this.state.closeButtonClickCount + 1;
            this.setState(
            {closeButtonClickCount: updatedClickCount,
             surveyResponseTimeout: setTimeout(() => {
                this.closeConnection();
            }, this.state.surveyTimeoutMax)
            }, () => {
                this.state.webSocketManager.sendUserInput('Get chat survey template');
            });
        }
        else {
            clearTimeout(this.state.surveyResponseTimeout);
            this.closeConnection();
            this.setState({surveyResponseTimeout: undefined});
        }
        this.state.analyticsManager.userClosedChatWindow();
    }

    /**
     * If the data returned from the websocket login flow handling exists and is true, send the
     * custom merge message to the bot. On failure or success, then delete the login flow cookie.
     */
    handleLoginFlowEnded() {
        if (!this.state.loginFlowInProgress) {
            return;
        }

        this.setState({loginFlowInProgress: false}, () => {
            if (this.state.chatCookieManager.readcookie(this.state.loginFlowInProgressCookieName) !== '' && this.state.chatConnected && this.state.loginFlowSuccess) {
                this.state.webSocketManager.sendUserMergeContinue();
                this.state.chatCookieManager.deleteCookie(this.state.loginFlowInProgressCookieName);
            }
        });
    }

    /**
     *
     * @param {MessageEvent} data The url from the custom login template to redirect the user.
     */
    handleLoginFlowStarted(data) {
        this.setState({loginFlowInProgress: true}, () => {
            const expirationTime = new Date(new Date().getTime() + (this.state.loginFlowInProgessTimeout * 60 * 1000));
            this.state.chatCookieManager.createCookie(this.state.loginFlowInProgressCookieName, 'true', expirationTime);
            if (data && data.trim() !== '') {
                window.location = data;
            }
        });
    }

    /**
     * Handles the chat timeout from the websocket manager. Holds some overlap with
     * hideChatWindow() and closeConnection but requires some different logic based on if
     * the chat is open/minimized as well as clearing the messages which wasn't a part of
     * either.
     */
    handleChatTimeout() {
        this.setState({
            messages: [],
            chatWindowOpen: false,
            chatConnected: false,
            awaitingResponse: false
        }, () => {
            if (this.state.fabButton.classList.contains('hidden')) {
                this.state.fabButton.classList.remove('hidden');
            }
            this.state.chatWindow.close();
            this.state.container.classList.remove('open');
            this.state.userInput.value = '';
            this.state.fabButtonMinimizedMessage.classList.remove('minimized');
            this.state.chatCookieManager.deleteCookie(this.state.chatInProgressCookieName);
        });
    }

    /**
     * Watches the user typing event in the input box. If the user is in an active
     * agent chat, then we clear any existing started/end intervals/timeouts, and
     * set new ones against the most recent keystroke. If the interval is already
     * set, then we return to throttle the interaction and WebSocket messages
     * being sent.
     */
    handleUserTypingEvent(e) {
        // This should be inferred from how the event is set but just to make sure
        if (!e?.key || this.typingStartedInterval) {
            return;
        }

        this.clearTypingTimeouts();

        this.typingStartedInterval = setInterval(() => {
            this.state.webSocketManager.sendTypingStarted();
        }, this.typingStartedDelay);

        this.typingEndedTimeout = setTimeout(() => {
            this.state.webSocketManager.sendTypingEnded();
            this.clearTypingTimeouts();
        }, this.typingEndedDelay);
    }

    /**
     * This handles when a websocket error is received from connection attempts.
     * This will only for now render a text message asking the user to close the bot
     * and re-open again, pending UX input.
     */
    handleWebsocketError() {
        const messageSender = 'bot';
        const message = {
            'messageSender': messageSender,
            'payload': {
                'type': 'WebsocketError'
            },
            'messageTime': Date.now(),
            'lastMessageSender': ''
        };
        this.setState({messages: [...this.state.messages, message], lastMessageSender: messageSender}, () => {
            this.state.chatWindow.querySelector('.chat-window__messages').lastElementChild.scrollIntoView({behavior: "smooth"});
        });
    }

    handleWsUrlUnavailable() {
        const messageSender = 'bot';
        const message = {
            'messageSender': messageSender,
            'payload': {
                'type': 'WebSocketUnavailable'
            },
            'messageTime': Date.now(),
            'lastMessageSender': ''
        };
        this.setState({messages: [...this.state.messages, message], lastMessageSender: messageSender}, () => {
            this.state.chatWindow.querySelector('.chat-window__messages').lastElementChild.scrollIntoView({behavior: 'smooth'});
            this.state.chatCookieManager.deleteCookie(this.state.chatInProgressCookieName);
        });
    }

    minimizeChatWindow() {
        if (this.state.chatConnected) {
            this.state.fabButtonMinimizedMessage.classList.add('minimized');
        }
        this.hideChatWindow();
    }

    /**
     * Focuses the user back onto the input bar. This is needed when a user clicks on
     * a button response or else it will focus them back on the <body>.
     */
    refocusInput() {
        this.state.userInput.focus();
    }


    renderChatHistory(messages) {
        let lastMessageSender = '';
        const mappedMessages = messages.map((message) => {
            let payload;
            /*Need this as the text can come back either as a string or as stringified JSON.
               Without the try catch, if JSON.parse is run on a generic text string it will
               fail. We also need this as the websocket messages return with both a stringified JSON
               response as well as a standard object response. The object response was used for
               mapping of messages initially but it was discovered that the chat history responses
               ONLY come back with the stringified JSON.
            */
            try {
                payload = JSON.parse(message.components[0].data.text);
            }
            catch (e) {
                payload = message.components[0].data.text;
            }
            const messageSender = message.type === 'outgoing' ? 'bot' : 'user';
            const mappedMessage = {
                'messageSender': message.type === 'outgoing' ? 'bot' : 'user',
                'payload': payload,
                'messageTime': message.createdOn,
                'lastMessageSender': lastMessageSender === '' ? messageSender : lastMessageSender,
                'isChatHistory': true
            };
            lastMessageSender = messageSender;
            return mappedMessage;
        });
        this.setState({messages: [...mappedMessages]}, () => {
            this.state.chatWindow.querySelector('.chat-window__messages').lastElementChild.scrollIntoView({behavior: 'instant'});
            this.refocusInput();
            if (this.newMessageLiveRegion.current) {
                this.newMessageLiveRegion.current.innerText = this.state.messages.length === 1 ? `1 previous message from chat history.` : `${this.state.messages.length} previous messages from chat history.`;
            }
        });
    }

    /**
     * Sets the global window variable for chatAuthenticated to true only if the flag
     * from the login system is set to true.
     */
    setAuthenticationStatus() {
        if (this.state.chatAuthenticated && this.state.chatAuthenticated.toLowerCase() === 'true') {
            window.chatAuthenticated = true;
        }
    }

    /**
     * Sets the event listeners for the chat object as a whole. This include custom
     * events for passthroughs like from the WebSocketManager as well as standard events such
     * as click and keydown.
     */
    setEventListeners() {
        document.addEventListener(ChatEventCodes.AgentSessionEnd, () => {
            this.handleAgentSessionEnd();
        });

        document.addEventListener(ChatEventCodes.AgentSessionStart, () => {
            this.handleAgentSessionStart();
        });

        document.addEventListener(ChatEventCodes.BotActive, (/** @type {MessageEvent} */ e) => {
            this.handleBotActive(e.data);
        });

        document.addEventListener(ChatEventCodes.BotResponse, (/** @type {MessageEvent} */e) => {
            this.handleBotResponse(e.data);
        });

        document.addEventListener(ChatEventCodes.BotKitUnreachable, (/** @type {MessageEvent} */e) => {
            this.handleBotKitUnreachable(e.data);
        });

        document.addEventListener(ChatEventCodes.ChatConnected, () => {
            this.handleChatConnected();
        });

        document.addEventListener(ChatEventCodes.ChatDisconnected, () => {
            this.chatDisconnected();
        });

        document.addEventListener(ChatEventCodes.ChatTimeout, () => {
            this.handleChatTimeout();
        });

        document.addEventListener(ChatEventCodes.FormSubmitted, (/** @type {MessageEvent} */e) => {
            this.submitFormData(e.data);
        });

        document.addEventListener(ChatEventCodes.LoginFlowStarted, (/**@type {MessageEvent} */e) => {
            console.log('login flow started');
            this.handleLoginFlowStarted(e.data);
        });

        document.addEventListener(ChatEventCodes.UpdateNewMessage, (/**@type {MessageEvent} */e) => {
            if (this.newMessageLiveRegion.current) {
                this.newMessageLiveRegion.current.innerText = e.data.updateMessage;
            }
        });

        document.addEventListener(ChatEventCodes.UserClickedInput, (/** @type {MessageEvent} */e) => {
            this.submitUserClickInput(e.data);
        });


        document.addEventListener(ChatEventCodes.UserMaskedInput, (/**@type {MessageEvent} */e) => {
            this.submitUserMaskedInput(e.data);
        });

        document.addEventListener(ChatEventCodes.WebsocketError, () => {
            this.handleWebsocketError();
        });

        document.addEventListener(ChatEventCodes.WebsocketServiceUnavailable, () => {
            this.handleWsUrlUnavailable();
        });

        this.state.fabButton.addEventListener('click', () => {
            if(this.state.chatWindowOpen) {
                this.hideChatWindow();
            }
            else {
                this.showChatWindow();
            }
        });

        for (const externalFabButton of this.state.externalFabButtons) {
            externalFabButton.addEventListener('click', (e) => {
                if(this.state.chatWindowOpen || (!this.state.chatWindowOpen && this.state.chatConnected)) {
                    return;
                }
                else {
                    /**
                     * Call the analytics manager for the ROL ask us handler if the fab-launcher
                     * button is one of the ones injected into ROL.
                     */
                    if (e.target.classList.contains('rol-ask-us')) {
                        this.state.analyticsManager.rolAskUsClicked();
                    }
                    this.showChatWindow();
                }
            });
        }


        this.state.userInput.addEventListener('keydown', (e) => {
            if (e.key === KeyCodes.Enter) {
                this.submitUserInput();
            }
        });

        this.state.sendButton.addEventListener('click', () => {
            this.submitUserInput();
        });

        /**
         * Assuming there can at some point be multiple close and minimize buttons
         * across the chat implementation.
         */
        for (const closeButton of this.state.closeButtons) {
            closeButton.addEventListener('click', () => {
                this.handleCloseButton();
            });
        }

        for (const minimizeButton of this.state.minimizeButtons) {
            minimizeButton.addEventListener('click', () => {
                this.minimizeChatWindow();
            });
        }
    }

    /**
     * Sends the user data through the socket connection and then updates the chat
     * in progress cookie timestamp.
     * @param {String} data The data to be sent through the websocket.
     */
    sendUserData(data) {
        this.state.webSocketManager.sendUserInput(data);
        this.state.chatCookieManager.updateCookie(this.state.chatInProgressCookieName, Date.now());
    }

    /**
     * Handles the message to the bot when a user is submitting a form. Used currently for the
     * Post Live and Post Bot surveys. This does not post anything to the chat window as of yet.
     * @param {Object} data The object with the form data collection as well as if the window should close automatically after submission.
     */
    submitFormData(data) {
        if (!this.state.chatWindowOpen && !this.state.chatConnected) {
            return;
        }
        this.state.webSocketManager.sendFormSubmission(data.formData);
        this.state.chatCookieManager.updateCookie(this.state.chatInProgressCookieName, Date.now());

        if (data.submitAndClose) {
            this.closeConnection();
        }
    }

    /**
     * Get the data from the user clicked input such as from a bot buttons template response.
     * If the window isn't open OR the chat isn't connected, ignore the input.
     * If the data is an empty string also do nothing. If valid, then create the message object and add it to the state
     * recorded messages array. Then clear out the user input field
     * @param {String} data The user input data. This can be from typing or interacting with a bot message.
     */
    submitUserClickInput(data) {
        if (!this.state.chatWindowOpen && !this.state.chatConnected) {
            return;
        }

        if (data.trim() === '') {
            return;
        }
        const messageSender = 'user';
        const message = {
            'messageSender': messageSender,
            'payload': data,
            'messageTime': Date.now(),
            'lastMessageSender': this.state.lastMessageSender
        }
        this.setState({messages: [...this.state.messages, message], lastMessageSender: messageSender, awaitingResponse: true}, () => {
            this.sendUserData(data);
            this.state.userInput.value = '';
            this.refocusInput();
            this.state.chatWindow.querySelector('.chat-window__messages').lastElementChild.scrollIntoView({behavior: "smooth"});
        });
    }

    /**
     * Submit the user input if they are interacting directly with the input box.
     * @returns
     */
    submitUserInput() {
        if (!this.state.chatWindowOpen && !this.state.chatConnected) {
            return;
        }

        const inputValue = this.state.userInput.value;
        if(inputValue.trim() === '') {
            return;
        }
        const messageSender = 'user';
        const message = {
            'messageSender': messageSender,
            'payload': inputValue,
            'messageTime': Date.now(),
            'lastMessageSender': this.state.lastMessageSender
        }
        this.setState({messages: [...this.state.messages, message], lastMessageSender: messageSender, awaitingResponse: true}, () => {
            this.sendUserData(inputValue);
            this.state.userInput.value = '';
            this.refocusInput();
            this.state.chatWindow.querySelector('.chat-window__messages').lastElementChild.scrollIntoView();
            this.state.analyticsManager.userSentMessage();
        });
    }

    /**
     * Get the data from the user clicked input such as from a bot buttons template response.
     * If the window isn't open OR the chat isn't connected, ignore the input.
     * If the data is an empty string also do nothing. If valid, then create the message object and add it to the state
     * recorded messages array. Then clear out the user input field. This will also include a making property to display
     * as the message in the chat window while a different value is sent to the bot.
     * @param {Object} data The user input data. This can be from typing or interacting with a bot message.
     */
    submitUserMaskedInput(data) {
        if (!this.state.chatWindowOpen && !this.state.chatConnected) {
            return;
        }

        if (data.botPayload.trim() === '' || data.displayMessage.trim() === '') {
            return;
        }
        const messageSender = 'user';
        const message = {
            'messageSender': messageSender,
            'payload': data.displayMessage,
            'messageTime': Date.now(),
            'lastMessageSender': this.state.lastMessageSender
        }
        this.setState({messages: [...this.state.messages, message], lastMessageSender: messageSender, awaitingResponse: true}, () => {
            this.sendUserData(data.botPayload);
            this.state.userInput.value = '';
            this.refocusInput();
            this.state.chatWindow.querySelector('.chat-window__messages').lastElementChild.scrollIntoView({behavior: "smooth"});
        });
    }

    render() {
        /**This is in case a user has a version of IE or iOS below 15.4 where the <dialog> element
         * is not supported. Rather render nothing to them instead of the chat window covering the
         * screen and being rendered unresponsive.
        */
        if (!this.state.renderChat) {
            return '';
        }
        /**
         * Note the inline style on the box prevents a case when the CSS has not loaded yet, but the JS executes
         * on componentDidMount. This was causing the user focus to move to the bottom of the <body> tag
         * if the window opened before the CSS was applied. This generally happened when there was a chat
         * in progress and and the window is auto opened for the user upon navigation to a new page.
         */
        return (
            <div id='kore-chat-bot' className='kore-chat-bot no-print' style='position: fixed;'>
                <button className='kore-chat-bot__fab chat-fab' title='Open the chat window.'>
                    <span className='chat-fab__minimized-message'>Chat in progress</span>
                </button>
                <dialog className='kore-chat-bot__chat-window chat-window__wrapper'>
                    <div className='chat-window'>
                        <ChatWindowHeader isConnecting = {this.state.isConnecting}></ChatWindowHeader>
                        <Messages messages = {this.state.messages}></Messages>
                        <AwaitingMessageHandler isWaiting = {this.state.awaitingResponse}></AwaitingMessageHandler>
                        <QueuePosition payload = {this.state.queuePositionPayload}></QueuePosition>
                        <div className='vh' id='newMessageLiveRegion' aria-live='assertive' ref={this.newMessageLiveRegion}></div>
                        <div className='chat-window__input-divider'></div>
                        <div className='chat-window__input-wrapper'>
                            <input type='text' className='chat-window__input' placeholder='Type your message here...'></input>
                            <button className='chat-window__send-message' title='Send message.' aria-label='Send a message'></button>
                        </div>
                    </div>
                </dialog>
                <link rel="stylesheet" href={this.state.cssUrl}></link>
            </div>
        )
    }
}